/* Route table and the shell every page sits inside.

   Every page is a separate chunk. The home screen is the only one a first-time
   visitor is guaranteed to need, and shipping the checkout, the address form
   and the order tracker to someone who is browsing is bytes spent on a screen
   they may never open.

   The fallback is a skeleton, not a spinner. A chunk resolves in tens of
   milliseconds on a warm cache, and a spinner that flashes for 40ms is worse
   than nothing — it draws the eye to a transition that was already invisible. */

import { Suspense, useEffect, useMemo } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TabBar } from './components/layout/TabBar'
import { DocumentTitle } from './components/layout/DocumentTitle'
import { LocaleSwap } from './components/region/LocaleSwap'
import { CartBar } from './components/cart/CartBar'
import { ToastHost } from './components/ui/Toast'
import { PageTransition } from './components/layout/PageTransition'
import { PageSkeleton } from './components/layout/PageSkeleton'
import { RouteBoundary } from './components/layout/RouteBoundary'
import { UpdatePrompt } from './pwa/UpdatePrompt'
import { UpdateDialog } from './components/layout/UpdateDialog'
import { useCities, useResolvedArea } from './lib/queries'
import { OfflineBanner } from './components/layout/OfflineBanner'
import { useCart } from './state/cart'
import { useMenu } from './lib/queries'
import { lineTotal } from './lib/api'
import { toast } from './components/ui/Toast'
import { useKeyboardInset } from './design/viewport'
import { lazyRoute } from './lib/lazyRoute'
import { TelemetryRoutes } from './lib/telemetry-react'

/* `lazyRoute`, not `lazy`: a chunk that 404s after a deploy or hangs on a bad
   connection has to end up as an error the RouteBoundary below can act on,
   rather than unmounting the app or suspending forever. See lib/lazyRoute. */
const Landing = lazyRoute(() => import('./pages/Landing'))
const Home = lazyRoute(() => import('./pages/Home'))
const Book = lazyRoute(() => import('./pages/Book'))
const Explore = lazyRoute(() => import('./pages/Explore'))
const Join = lazyRoute(() => import('./pages/Join'))
/* Not a form any more — see the note in the file. Applying is the partner
   app's job, and this site kept a second copy of it for as long as this import
   said `JoinForm`. */
const JoinRedirect = lazyRoute(() => import('./pages/JoinRedirect'))
const Search = lazyRoute(() => import('./pages/Search'))
const ProviderPage = lazyRoute(() => import('./pages/Provider'))
const Basket = lazyRoute(() => import('./pages/Basket'))
const Checkout = lazyRoute(() => import('./pages/Checkout'))
const OrderTrack = lazyRoute(() => import('./pages/OrderTrack'))
const Orders = lazyRoute(() => import('./pages/Orders'))
const Account = lazyRoute(() => import('./pages/Account'))
const Addresses = lazyRoute(() => import('./pages/Addresses'))
const Auth = lazyRoute(() => import('./pages/Auth'))
const AuthAction = lazyRoute(() => import('./pages/AuthAction'))
const NotFound = lazyRoute(() => import('./pages/NotFound'))

/** The basket subtotal, computed once here and handed to the bar — and the one
 *  place a line whose item has left the price list is dealt with.
 *
 *  It needs the price list to price its lines, and that list is already in the
 *  cache from the screen the items were added on — so this is a cache read in
 *  practice, not a request.
 *
 *  ── Why the pruning lives here ──────────────────────────────────────────────
 *  A basket is client-owned and outlives the catalogue behind it. An item the
 *  partner withdraws, or a list the panel rebuilds, leaves a line pointing at
 *  an id nothing answers to any more — and every screen that renders a basket
 *  used to skip such a line in silence. The row returned `null`, the subtotal
 *  added nothing, and `count` went on counting it.
 *
 *  What that adds up to on screen is the basket bar reading "2" and "SAR 0.00"
 *  above a basket page with no rows in it: a basket that is empty and not empty
 *  at the same time, that cannot be checked out and cannot be emptied, because
 *  the only control that would remove a line is the row that refuses to render.
 *  There is no gesture that gets a customer out of that state.
 *
 *  Here rather than on the basket screen because the bar is on every screen and
 *  the basket screen is the one place a customer in this state has no reason to
 *  go — they can see there is nothing in it.
 *
 *  Only ever on a *successful* load. While the menu is pending or failed every
 *  id is unknown, and emptying somebody's basket because a request timed out
 *  would be a far worse bug than the one this fixes. */
function useBasketSubtotal(): number {
  const { t } = useTranslation()
  const { lines, listId, remove } = useCart()
  const menu = useMenu(listId ?? undefined)

  const byId = useMemo(
    () => new Map((menu.data?.items ?? []).map((i) => [i.id, i])),
    [menu.data],
  )

  useEffect(() => {
    if (!menu.isSuccess) return
    const gone = lines.filter((l) => !byId.has(l.itemId))
    if (gone.length === 0) return
    for (const line of gone) remove(line.key)
    /* Said out loud. A basket that quietly loses a line is the same class of
       bug as one that quietly keeps a phantom — the customer has to be able to
       tell that something changed and that it was not them. */
    toast.show(t(gone.length === 1 ? 'basket.withdrawnOne' : 'basket.withdrawnMany'))
  }, [menu.isSuccess, byId, lines, remove, t])

  return useMemo(
    () =>
      lines.reduce((sum, l) => {
        const item = byId.get(l.itemId)
        return item ? sum + lineTotal(l, item) : sum
      }, 0),
    [lines, byId],
  )
}

export default function App() {
  const subtotal = useBasketSubtotal()

  /* The markets, fetched once for the session. Read here rather than on the
     screens that pick from it because `addressLine` reaches
     `cityLabelForDistrict` from half a dozen places — a saved address, a
     receipt, an order's delivery line — and it is a pure formatter with no
     access to a query. Without it the second line renders no city at all for a
     district that is not in this build's bundled table, which is every district
     an operator adds from the panel. */
  useCities()

  /* Keeps the chosen district on something the API actually serves — the first
     one on a fresh visit, and a served one if the district somebody saved has
     since been switched off. Without it, a visitor returning after a market
     change opens the app on a district with no partners and no explanation. */
  useResolvedArea()

  /* Publishes `--kb` so the tab bar, the cart bar and the toasts stay above an
     on-screen keyboard instead of behind it. */
  useKeyboardInset()

  return (
    <div className="app-shell">
      {/* Sets `document.title`, which is also what the route announcer below
          reads out after every navigation. */}
      <DocumentTitle />
      <OfflineBanner />
      {/* Renders nothing. Records a screen view per navigation, and the trail
          of screens behind whatever fails next. */}
      <TelemetryRoutes />

      <main className="app-main" id="main">
        {/* Inside `<main>`, so a route that cannot render costs the user that
            route and not the tab bar underneath it. */}
        <RouteBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <PageTransition>
              <Routes>
                {/* `/` sells, `/app` works. Two audiences, two jobs: a
                    first-time visitor needs to know what this is before being
                    handed a category grid, and a returning customer should
                    never be made to scroll past a pitch to reach their
                    basket. */}
                <Route path="/" element={<Landing />} />
                <Route path="/app" element={<Home />} />
                {/* The default way in to every service: a price list and a
                    stepper. No shop to pick, because we pick it. */}
                <Route path="/book/:category" element={<Book />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/join" element={<Join />} />
                <Route path="/join/:kind" element={<JoinRedirect />} />
                <Route path="/search" element={<Search />} />
                {/* Not a browse destination. Nothing links here except a
                    basket, a checkout or an order that already names the
                    forecourt the customer is driving to — see Provider.tsx. */}
                <Route path="/p/:slug" element={<ProviderPage />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:id" element={<OrderTrack />} />
                <Route path="/orders" element={<Orders />} />
                {/* Saved shops are gone with the rest of partner discovery.
                    A redirect rather than a 404 because the tab was in the bar
                    for months and is in people's history and home screens. */}
                <Route path="/saved" element={<Navigate to="/app" replace />} />
                <Route path="/account" element={<Account />} />
                <Route path="/account/addresses" element={<Addresses />} />
                <Route path="/auth" element={<Auth />} />
                {/* Where the identity provider's account mails land — reset,
                    verify, undo an address change. A route rather than a screen
                    reached from inside the app, because nothing in the app
                    links here: the way in is a link in an inbox, and the
                    single-use code is in the query string. */}
                <Route path="/auth/action" element={<AuthAction />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageTransition>
          </Suspense>
        </RouteBoundary>
      </main>

      <CartBar subtotal={subtotal} />
      <TabBar />
      <ToastHost />
      <UpdatePrompt />
      {/* The other half of the same job: the bar above hears from the service
          worker, this asks the server directly. Together they cover the tab
          that was open across a deploy, which neither did on its own. */}
      <UpdateDialog />
      {/* Covers the reflow when the writing direction flips. Portalled above
          everything, including the sheet the switch was usually pressed in. */}
      <LocaleSwap />

      <span id="route-announcer" className="sr-only" role="status" aria-live="polite" />
    </div>
  )
}
